import { useState } from "react";
import PageBanner from "../components/PageBanner";
import { Phone, MessageCircle, Mail, MapPin, User, Send, Building2 } from "lucide-react";
import { company } from "../mock/mock";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    setTimeout(() => {
      try {
        const list = JSON.parse(localStorage.getItem("mlt_inquiries") || "[]");
        list.unshift({ ...form, at: new Date().toISOString() });
        localStorage.setItem("mlt_inquiries", JSON.stringify(list.slice(0, 25)));
      } catch (_) {}
      toast.success("Thank you! Your inquiry has been received. We will get back shortly.");
      setForm({ name: "", company: "", email: "", phone: "", subject: "", message: "" });
      setLoading(false);
    }, 700);
  };

  return (
    <div>
      <PageBanner
        title="Contact Us"
        subtitle="Reach out for product inquiries, bulk orders or custom solutions"
        breadcrumbs={[{ label: "Contact Us" }]}
      />

      <section className="py-14">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-3 gap-8">
          {/* Info */}
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-[#0d2240] text-white rounded-xl p-6">
              <h3 className="font-heading font-bold text-xl">{company.name}</h3>
              <p className="text-white/70 text-sm mt-2">Manufacturers, suppliers, wholesalers, traders & retailers of premium adhesive tapes.</p>
            </div>

            <a href={`tel:+91${company.phone}`} className="flex items-center gap-3 bg-white p-4 rounded-xl border border-gray-100 hover:border-[#c8102e]/40 transition-colors">
              <span className="w-11 h-11 rounded-md grid place-items-center bg-[#c8102e]/10 text-[#c8102e]"><Phone size={18} /></span>
              <div>
                <p className="text-[11px] uppercase tracking-widest text-gray-500">Phone (Click to Call)</p>
                <p className="font-heading font-semibold text-[#0d2240]">+91 {company.phone}</p>
              </div>
            </a>

            <a href={`https://wa.me/91${company.whatsapp}?text=Hello%20Mahalaxmi%20Tape%20Industries`} target="_blank" rel="noreferrer" className="flex items-center gap-3 bg-white p-4 rounded-xl border border-gray-100 hover:border-[#25D366]/40 transition-colors">
              <span className="w-11 h-11 rounded-md grid place-items-center bg-[#25D366]/10 text-[#25D366]"><MessageCircle size={18} /></span>
              <div>
                <p className="text-[11px] uppercase tracking-widest text-gray-500">WhatsApp (Click to Chat)</p>
                <p className="font-heading font-semibold text-[#0d2240]">+91 {company.whatsapp}</p>
              </div>
            </a>

            <a href={`mailto:${company.email}`} className="flex items-center gap-3 bg-white p-4 rounded-xl border border-gray-100 hover:border-[#c8102e]/40 transition-colors">
              <span className="w-11 h-11 rounded-md grid place-items-center bg-[#c8102e]/10 text-[#c8102e]"><Mail size={18} /></span>
              <div>
                <p className="text-[11px] uppercase tracking-widest text-gray-500">Email (Click to Mail)</p>
                <p className="font-heading font-semibold text-[#0d2240] break-all">{company.email}</p>
              </div>
            </a>

            <div className="flex items-start gap-3 bg-white p-4 rounded-xl border border-gray-100">
              <span className="w-11 h-11 rounded-md grid place-items-center bg-[#c8102e]/10 text-[#c8102e] shrink-0"><MapPin size={18} /></span>
              <div>
                <p className="text-[11px] uppercase tracking-widest text-gray-500">Address</p>
                <p className="text-sm text-[#0d2240] leading-relaxed mt-1">{company.address}</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-gray-100 p-6 md:p-8">
              <h3 className="font-heading font-extrabold text-2xl text-[#0d2240]">Quick Inquiry</h3>
              <p className="text-sm text-gray-500 mt-1">Fill the form below and our team will contact you shortly.</p>
              <form onSubmit={onSubmit} className="mt-6 grid sm:grid-cols-2 gap-4">
                <Field icon={User} label="Full Name *">
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" placeholder="Your full name" />
                </Field>
                <Field icon={Building2} label="Company">
                  <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="input" placeholder="Company name" />
                </Field>
                <Field icon={Mail} label="Email *">
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" placeholder="you@example.com" />
                </Field>
                <Field icon={Phone} label="Phone">
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input" placeholder="+91 XXXXX XXXXX" />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Subject">
                    <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="input" placeholder="Inquiry about BOPP / Masking / VHB..." />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Message *">
                    <textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="input" placeholder="Type your requirement, quantity, sizes..." />
                  </Field>
                </div>
                <div className="sm:col-span-2 flex flex-wrap gap-3 mt-1">
                  <button disabled={loading} type="submit" className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-[#c8102e] hover:bg-[#9a0c24] text-white text-sm font-semibold disabled:opacity-60">
                    {loading ? "Sending..." : <><Send size={15} /> Send Inquiry</>}
                  </button>
                  <a href={`https://wa.me/91${company.whatsapp}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-[#25D366] hover:bg-[#1ebe5a] text-white text-sm font-semibold">
                    <MessageCircle size={15} /> Chat on WhatsApp
                  </a>
                </div>
              </form>
            </div>

            <div className="mt-6 rounded-xl overflow-hidden border border-gray-100">
              <iframe
                title="Mahalaxmi Tape Industries Map"
                src="https://www.google.com/maps?q=Dashrath+Vadodara+Gujarat+391740&output=embed"
                width="100%"
                height="320"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .input { width: 100%; padding: 12px 14px; border: 1px solid #e5e7eb; border-radius: 8px; font-size: 14px; transition: border-color .2s, box-shadow .2s; background:#fff; }
        .input:focus { border-color: #c8102e; box-shadow: 0 0 0 3px rgba(200,16,46,0.12); }
      `}</style>
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <label className="block">
      <span className="text-[12px] font-semibold text-[#0d2240] uppercase tracking-wider mb-1.5 inline-flex items-center gap-1">
        {Icon && <Icon size={12} className="text-[#c8102e]" />}
        {label}
      </span>
      {children}
    </label>
  );
}
